import React, { ReactElement, useEffect, useMemo } from 'react';
import { dispatchMarketApps, useMarketApps } from 'adapter/in/dashboards/context/MarketApps';
import MarketAppTable from 'adapter/in/dashboards/pages/Dashboards/marketAppTable';
import { useNavigation } from 'adapter/in/common/hooks/useNavigation';
import i18n from 'asset/translation';
import { MarketAppView, MarketAppVisibility } from 'domain/entity/market/marketApp';
import { isNone, isSome, Option } from 'fp-ts/lib/Option';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { marketAppView } from 'adapter/in/common/routes';
import { dispatchLocation, useLocation } from 'adapter/in/dashboards/context/Location';
import { ReadLocationCriteriaBuilder } from 'use-case/location/readLocationCriteria';
import { ReadMarketAppsCriteriaBuilder } from 'use-case/marketApps/read/readMarketAppsCriteria';
import { LocationType } from 'domain/entity/location';
import { ENVIRONMENT } from 'domain/entity/environment';
import styled from 'styled-components';

const StyledColumnTable = styled.a`
    color: black;
`;

const getColumn = (rootURL: string) => [
    {
        title: i18n.t('dashboards.marketAppNameTitle'),
        dataIndex: '',
        render: (marketApp: MarketAppView) => (
            <StyledColumnTable href={`${rootURL}${marketAppView}/${marketApp.id}.htm`} aria-label='market app'>
                <span>{marketApp.title}</span>
            </StyledColumnTable>
        ),
    },
];

type UseMarketAppReturn = {
    installedMarketApps: Option<MarketAppView[]>;
    href: string;
    isLoading: boolean;
    showError: boolean;
    hasMarketAppsAccess: boolean;
};

export const mapVisibility = (locationtype: LocationType): MarketAppVisibility => {
    switch (locationtype) {
        case LocationType.LEAF:
        case LocationType.SECTION:
            return MarketAppVisibility.DASHBOARD_LOCATION;

        case LocationType.BRANCH:
        case LocationType.ROOT:
        default:
            return MarketAppVisibility.DASHBOARD_ZONE;
    }
};
const useMarketAppTableContainer = (pathUrl: string): UseMarketAppReturn => {
    const { currentUrl } = useNavigation();
    const { marketApps, isLoading, error: marketAppError } = useMarketApps();
    const { read: readMarketApps } = dispatchMarketApps();
    const { read: readLocation } = dispatchLocation();
    const { location, error: locationError } = useLocation();
    const { profile } = useProfile();

    useEffect(() => {
        if (isSome(location) && isNone(marketApps)) {
            readMarketApps(ReadMarketAppsCriteriaBuilder.create().withVisibility(mapVisibility(location.value.type)).build());
        }
    }, [location]);

    useEffect(() => {
        readLocation(ReadLocationCriteriaBuilder.create().withLocationId(profile.locationId).build());
    }, [profile]);

    const rootUrl: string = useMemo(() => {
        let joinedURL = '';
        if (process.env.ENV !== ENVIRONMENT.DEVELOPMENT) {
            const splitURL = currentUrl.split('/');
            splitURL.length = splitURL.indexOf(pathUrl.replace('/', ''));
            const findLocationOnURLRegEx = /^l_/;
            const hasLocation = splitURL.some((item: string) => findLocationOnURLRegEx.test(item));
            if (!hasLocation) {
                splitURL.push(`l_${profile.locationId}`);
            }
            joinedURL = splitURL.join('/');
            if (!joinedURL.endsWith('/')) {
                joinedURL += '/';
            }
        }
        return joinedURL;
    }, [currentUrl, profile, pathUrl]);

    return {
        installedMarketApps: marketApps,
        href: rootUrl,
        isLoading,
        hasMarketAppsAccess: isSome(marketApps) || (isSome(marketAppError) && marketAppError.value.status !== 403),
        showError: (isSome(marketAppError) && marketAppError.value.status !== 403) || isSome(locationError),
    };
};

type MarketAppTableProps = {
    pathUrl: string;
};
const MarketAppTableContainer = (props: MarketAppTableProps): ReactElement => {
    const { pathUrl } = props;
    const { installedMarketApps, href, isLoading, showError, hasMarketAppsAccess } = useMarketAppTableContainer(pathUrl);

    return (
        <>
            {hasMarketAppsAccess && (
                <MarketAppTable
                    columns={isSome(installedMarketApps) ? getColumn(href) : null}
                    isLoading={isLoading}
                    showError={showError}
                    dataSource={isSome(installedMarketApps) ? installedMarketApps.value : null}
                />
            )}
        </>
    );
};

export default MarketAppTableContainer;
